import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { AuthCallback } from './pages/auth/AuthCallback';
import { DevLogin } from './pages/auth/DevLogin';
import { SessionLoadingPage } from './pages/auth/SessionLoadingPage';
import { SessionErrorPage } from './pages/auth/SessionErrorPage';
import { NoAccessPage } from './pages/auth/NoAccessPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { CampaignsPage } from './pages/campaigns/CampaignsPage';
import { CampaignBuilderPage } from './pages/campaigns/CampaignBuilderPage';
import { CampaignTriggersPage } from './pages/campaigns/CampaignTriggersPage';
import { CampaignAudiencePage } from './pages/campaigns/CampaignAudiencePage';
import { CampaignSequencePage } from './pages/campaigns/CampaignSequencePage';
import { CampaignSettingsPage } from './pages/campaigns/CampaignSettingsPage';
import { CampaignReviewPage } from './pages/campaigns/CampaignReviewPage';
import { CampaignDetailPage } from './pages/campaigns/CampaignDetailPage';
import { CampaignReportPage } from './pages/campaigns/CampaignReportPage';
import { WorkflowBuilderPage } from './pages/campaigns/WorkflowBuilderPage';
import { AudiencesPage } from './pages/audiences/AudiencesPage';
import { InboxPage } from './pages/inbox/InboxPage';
import { TemplatesPage } from './pages/templates/TemplatesPage';
import { TemplateBuilderPage } from './pages/templates/TemplateBuilderPage';
import { CustomHtmlTemplatePage } from './pages/templates/CustomHtmlTemplatePage';
import { FormsPage } from './pages/forms/FormsPage';
import { FormBuilderPage } from './pages/forms/FormBuilderPage';
import { FormSubmissionsPage } from './pages/forms/FormSubmissionsPage';
import { SurveysPage } from './pages/surveys/SurveysPage';
import { SurveyBuilderPage } from './pages/surveys/SurveyBuilderPage';
import { SurveyResultsPage } from './pages/surveys/SurveyResultsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { ComponentLibraryPage } from './pages/components/ComponentLibraryPage';
import { AuthGuard } from './components/guards/AuthGuard';
import { SystemRoleGuard } from './components/guards/SystemRoleGuard';
import { RestrictedLayout } from './pages/dashboard/restricted/RestrictedLayout';
import { OrganisationsPage } from './pages/dashboard/restricted/OrganisationsPage';
import { AccountsPage } from './pages/dashboard/restricted/AccountsPage';
import { SystemPage } from './pages/dashboard/restricted/SystemPage';

const isDev = import.meta.env.DEV;

export const router = createBrowserRouter([
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  { path: '/auth/loading', element: <SessionLoadingPage /> },
  { path: '/auth/session-expired', element: <SessionErrorPage /> },
  // Access refused / revoked / impersonation ended. Outside AuthGuard.
  { path: '/auth/no-access', element: <NoAccessPage /> },
  ...(isDev ? [{ path: '/dev-login', element: <DevLogin /> }] : []),
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'campaigns', element: <CampaignsPage /> },
      { path: 'campaigns/new', element: <CampaignBuilderPage /> },
      { path: 'campaigns/new/audience', element: <Navigate to="/campaigns/new" replace /> },
      { path: 'campaigns/new/sequence', element: <Navigate to="/campaigns/new" replace /> },
      { path: 'campaigns/new/settings', element: <Navigate to="/campaigns/new" replace /> },
      { path: 'campaigns/new/review', element: <Navigate to="/campaigns/new" replace /> },
      { path: 'campaigns/:id/edit', element: <CampaignBuilderPage /> },
      { path: 'campaigns/:id/triggers', element: <CampaignTriggersPage /> },
      { path: 'campaigns/:id/audience', element: <CampaignAudiencePage /> },
      { path: 'campaigns/:id/sequence', element: <CampaignSequencePage /> },
      { path: 'campaigns/:id/settings', element: <CampaignSettingsPage /> },
      { path: 'campaigns/:id/review', element: <CampaignReviewPage /> },
      { path: 'campaigns/:id/workflow', element: <WorkflowBuilderPage /> },
      { path: 'campaigns/:id/report', element: <CampaignReportPage /> },
      { path: 'campaigns/:id', element: <CampaignDetailPage /> },
      { path: 'audiences', element: <AudiencesPage /> },
      { path: 'inbox', element: <InboxPage /> },
      { path: 'templates', element: <TemplatesPage /> },
      { path: 'templates/new', element: <TemplateBuilderPage /> },
      { path: 'templates/html/new', element: <CustomHtmlTemplatePage /> },
      { path: 'templates/html/:id/edit', element: <CustomHtmlTemplatePage /> },
      { path: 'templates/:id/edit', element: <TemplateBuilderPage /> },
      { path: 'forms', element: <FormsPage /> },
      { path: 'forms/:id/submissions', element: <FormSubmissionsPage /> },
      { path: 'forms/:id', element: <FormBuilderPage /> },
      { path: 'surveys', element: <SurveysPage /> },
      { path: 'surveys/:id/results', element: <SurveyResultsPage /> },
      { path: 'surveys/:id', element: <SurveyBuilderPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'settings/*', element: <SettingsPage /> },
      { path: 'components', element: <ComponentLibraryPage /> },
      {
        // Super-admin surface. Gate is defence in depth — server enforces the
        // real check on /system/* per Section 13B.
        path: 'dashboard/restricted',
        element: (
          <SystemRoleGuard>
            <RestrictedLayout />
          </SystemRoleGuard>
        ),
        children: [
          { index: true, element: <OrganisationsPage /> },
          { path: 'organisations', element: <OrganisationsPage /> },
          { path: 'accounts', element: <AccountsPage /> },
          { path: 'system', element: <SystemPage /> },
        ],
      },
    ],
  },
]);
